import { memo, useEffect, useMemo, useRef, useState } from 'react';
import {
  ErrorLoad,
  Interpolate,
  Message,
  Paginator,
  Spinner,
  StepCSSTransitionGroup,
  Table,
  TableBody,
} from '@wg/wows-react-uikit';

import settings from '~/settings';
import { scrollToY } from '~/helpers/animate';
import { t } from '~/helpers/localization';

import styles from './ClansRatingSearchResult.scss';
import ClansRatingSearchResultTableHead from './ClansRatingSearchResultTableHead';
import ClansRatingSearchResultTableItem from './ClansRatingSearchResultTableItem';

import type { LeagueDivisionRatingType } from '~/Actions/ActionClanRating';
import type { ISearchState } from '~/Reducers/ReducerSearch';
import type { ICurrentAccountState } from '~/store/slices/currentAccountSlice';
import type { ClanSearchRatingResultItem } from '~/types/declaration';

interface ClansRatingSearchResultProps {
  currentAccount: ICurrentAccountState;
  currentSeason?: number;
  isCrossRealmLadder: boolean;
  searchResult: ISearchState;

  onItemClick: (params?: LeagueDivisionRatingType) => void;
  onPageChanged: (page: number) => Promise<void>;
  onReloadClick: () => Promise<void>;
}

const STICKY_CONTAINER_ID = 'search-result-sticky-container';

const ClansRatingSearchResult = ({
  currentAccount,
  currentSeason,
  searchResult,
  onItemClick,
  onPageChanged,
  onReloadClick,
}: ClansRatingSearchResultProps) => {
  const appRef = useRef<HTMLElement | null>(null);
  const stickyRef = useRef<HTMLElement | null>(null);

  const prevPageRef = useRef<number>(searchResult.page);
  const [fetchByNewPage, setFetchByNewPage] = useState(false);

  useEffect(() => {
    if (!appRef.current) {
      appRef.current = document.getElementById('app');
    }
  }, []);

  useEffect(() => {
    const prevPage = prevPageRef.current;
    const isPageChanged = prevPage !== searchResult.page;

    setFetchByNewPage(isPageChanged);

    if (isPageChanged) {
      if (!stickyRef.current) {
        stickyRef.current = document.getElementById(STICKY_CONTAINER_ID);
      }

      const appEl = appRef.current;
      const stickyEl = stickyRef.current;

      if (appEl && stickyEl) {
        scrollToY(appEl, stickyEl.offsetTop - appEl.offsetTop);
      }
    }

    prevPageRef.current = searchResult.page;
  }, [searchResult.page]);

  const items = searchResult.items as ClanSearchRatingResultItem[];
  const hasItems = items.length > 0;

  const { paginator, displayedTotal } = useMemo(() => {
    if (!searchResult.meta) {
      return { paginator: null as React.ReactNode, displayedTotal: '0' };
    }

    const total = searchResult.meta.total_clans!;
    const displayed =
      total > settings.search.searchResultMaxItems ? `${settings.search.searchResultMaxItems}+` : String(total);

    const p = (
      <Paginator
        key="paginator"
        page={searchResult.page}
        total={total}
        limit={settings.search.limit}
        isFetching={fetchByNewPage}
        isDisabled={searchResult.isFetching}
        onSelectedPage={onPageChanged as (page: number) => void}
      />
    );

    return { paginator: p, displayedTotal: displayed };
  }, [fetchByNewPage, onPageChanged, searchResult.isFetching, searchResult.meta, searchResult.page]);

  if (searchResult.isFetching && !hasItems) {
    return (
      <div className={styles.spinner}>
        <Spinner label={t('Ищем кланы')} />
      </div>
    );
  }

  if (searchResult.error) {
    return (
      <StepCSSTransitionGroup level={1}>
        <ErrorLoad
          isFlat
          key="error-load"
          message={t('Не удалось получить информацию о кланах.')}
          onReloadClick={onReloadClick as () => void}
        />
      </StepCSSTransitionGroup>
    );
  }

  if (!hasItems && !searchResult.isFetching) {
    return (
      <Message type="empty" style="inline">
        {t('Кланы с таким названием или тегом не найдены')}
      </Message>
    );
  }

  return (
    <StepCSSTransitionGroup level={1}>
      <div key="total" className={styles.total}>
        <Interpolate
          str={t('Найденные кланы: %(totalCount)s')}
          totalCount={<span className={styles.totalValue}>{displayedTotal}</span>}
        />
      </div>

      <Table key="table" isFetching={searchResult.isFetching} stickyContainerId={STICKY_CONTAINER_ID}>
        <ClansRatingSearchResultTableHead stickyContainerId={STICKY_CONTAINER_ID} />
        <TableBody>
          {items.map((clan) => (
            <ClansRatingSearchResultTableItem
              key={clan.id}
              clan={clan}
              term={searchResult.term}
              currentAccountClanId={currentAccount.clanId || undefined}
              currentSeason={currentSeason}
              onClick={onItemClick}
            />
          ))}
        </TableBody>
      </Table>

      {paginator}
    </StepCSSTransitionGroup>
  );
};

export default memo(ClansRatingSearchResult);
